'use client';

import { useState, useCallback, useEffect } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  Link2,
  RefreshCw,
  FileText,
  Package,
  Receipt,
  ArrowRight,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { authService } from '@/lib/authService';

interface ThreeWayMatchLineVariance {
  description: string;
  po?: { quantity: number; unit_price: number; total: number };
  grn?: { quantity: number; unit_price: number; total: number };
  expense?: { quantity: number; unit_price: number; total: number };
  quantity_variance: number;
  price_variance: number;
  total_variance: number;
  status: string;
}

interface ThreeWayMatchData {
  expense_id: string;
  expense_number: string;
  po_id?: string;
  po_number?: string;
  grn_id?: string;
  grn_number?: string;
  po_total?: number;
  grn_total?: number;
  expense_total?: number;
  total_variance: number;
  line_variances: ThreeWayMatchLineVariance[];
  status: 'matched' | 'pending' | 'variance' | 'no_po' | 'no_grn' | 'no_bom';
  checked_at: string;
}

interface ThreeWayMatchPanelProps {
  expenseId: string;
}

function statusBadge(status: string) {
  switch (status) {
    case 'matched':
      return {
        color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200',
        icon: CheckCircle2,
        label: 'Matched',
      };
    case 'variance':
      return {
        color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200',
        icon: AlertTriangle,
        label: 'Variance',
      };
    case 'no_po':
      return {
        color: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-200',
        icon: Link2,
        label: 'No PO Linked',
      };
    case 'no_grn':
      return {
        color: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-200',
        icon: Package,
        label: 'No GRN Linked',
      };
    default:
      return {
        color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300',
        icon: RefreshCw,
        label: 'Pending',
      };
  }
}

function lineStatusBadge(status: string) {
  switch (status) {
    case 'matched':
      return 'text-emerald-600';
    case 'quantity_variance':
      return 'text-amber-600';
    case 'price_variance':
      return 'text-amber-600';
    case 'missing_on_po':
      return 'text-red-500';
    case 'missing_on_expense':
      return 'text-red-500';
    default:
      return 'text-gray-400';
  }
}

export default function ThreeWayMatchPanel({ expenseId }: ThreeWayMatchPanelProps) {
  const [data, setData] = useState<ThreeWayMatchData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const loadMatch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const res = await fetch(`/api/v1/work-items/${expenseId}/three-way-match`, {
        headers: { Authorization: `Bearer ${token || ''}` },
      });
      const json = await res.json();
      if (json.success && json.data) {
        setData(json.data as ThreeWayMatchData);
      } else {
        setError(json.error || 'Failed to load match data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load match data');
    } finally {
      setLoading(false);
    }
  }, [expenseId]);

  useEffect(() => {
    void loadMatch();
  }, [loadMatch]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1.5 text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
        <RefreshCw className="h-3 w-3 animate-spin" />
        Checking three-way match…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
        <AlertTriangle className="h-3 w-3" />
        {error}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const badge = statusBadge(data.status);
  const BadgeIcon = badge.icon;
  const hasVariance = data.status === 'variance' || data.total_variance !== 0;

  return (
    <div data-tour-id="supplier-invoice-matching">
      {/* Compact header — always visible, same height as the supplier-required warning */}
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.color}`}>
            <BadgeIcon className="h-3 w-3" />
            {badge.label}
          </span>
          <span className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
            {data.po_number ? `PO ${data.po_number}` : 'No PO linked'}
            {data.grn_number ? ` → GRN ${data.grn_number}` : ''}
          </span>
          {hasVariance && (
            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
              R{data.total_variance.toFixed(2)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); void loadMatch(); }}
            className="rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/5"
            title="Refresh match"
          >
            <RefreshCw className="h-3 w-3" style={{ color: 'var(--theme-text-secondary)' }} />
          </button>
          {expanded
            ? <ChevronDown className="h-3.5 w-3.5" style={{ color: 'var(--theme-text-secondary)' }} />
            : <ChevronRight className="h-3.5 w-3.5" style={{ color: 'var(--theme-text-secondary)' }} />}
        </div>
      </div>

      {/* Expanded detail — only when user clicks to expand */}
      {expanded && (
        <div
          className="mt-2 rounded-lg border p-3"
          style={{
            backgroundColor: 'var(--theme-surface)',
            borderColor: 'var(--theme-border)',
          }}
        >
          {/* Document chain */}
          <div className="flex items-center gap-2 mb-3 text-xs">
            {data.po_number ? (
              <div className="flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-blue-700 dark:bg-blue-950/30 dark:text-blue-200">
                <FileText className="h-3 w-3" />
                <span className="font-medium">{data.po_number}</span>
              </div>
            ) : (
              <div className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-400 dark:bg-gray-800 text-[10px]">
                No PO
              </div>
            )}
            <ArrowRight className="h-3 w-3 text-gray-400" />
            {data.grn_number ? (
              <div className="flex items-center gap-1 rounded-md bg-purple-50 px-2 py-0.5 text-purple-700 dark:bg-purple-950/30 dark:text-purple-200">
                <Package className="h-3 w-3" />
                <span className="font-medium">{data.grn_number}</span>
              </div>
            ) : (
              <div className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-400 dark:bg-gray-800 text-[10px]">
                No GRN
              </div>
            )}
            <ArrowRight className="h-3 w-3 text-gray-400" />
            <div className="flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200">
              <Receipt className="h-3 w-3" />
              <span className="font-medium">{data.expense_number}</span>
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-md border p-1.5 text-center" style={{ borderColor: 'var(--theme-border)' }}>
              <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--theme-text-secondary)' }}>PO</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--theme-text-primary)' }}>
                R{data.po_total?.toFixed(2) ?? '—'}
              </p>
            </div>
            <div className="rounded-md border p-1.5 text-center" style={{ borderColor: 'var(--theme-border)' }}>
              <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--theme-text-secondary)' }}>GRN</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--theme-text-primary)' }}>
                R{data.grn_total?.toFixed(2) ?? '—'}
              </p>
            </div>
            <div className="rounded-md border p-1.5 text-center" style={{ borderColor: 'var(--theme-border)' }}>
              <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--theme-text-secondary)' }}>Invoice</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--theme-text-primary)' }}>
                R{data.expense_total?.toFixed(2) ?? '—'}
              </p>
            </div>
          </div>

          {/* Variance summary */}
          {data.total_variance !== 0 && (
            <div className="mb-3 rounded-md bg-amber-50 p-1.5 text-center text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
              Total variance: <span className="font-semibold">R{data.total_variance.toFixed(2)}</span>
            </div>
          )}

          {/* Line-item variances */}
          {data.line_variances.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-medium" style={{ color: 'var(--theme-text-secondary)' }}>
                Line-item variances
              </p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {data.line_variances.map((line, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded-md border px-2 py-1 text-[11px]"
                    style={{ borderColor: 'var(--theme-border)' }}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium" style={{ color: 'var(--theme-text-primary)' }}>
                        {line.description || 'Unnamed line'}
                      </p>
                      <p className={lineStatusBadge(line.status)}>
                        {line.status.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <div className="text-right tabular-nums">
                      {line.po && (
                        <p style={{ color: 'var(--theme-text-secondary)' }}>
                          PO: {line.po.quantity} × R{line.po.unit_price.toFixed(2)} = R{line.po.total.toFixed(2)}
                        </p>
                      )}
                      {line.grn && (
                        <p style={{ color: 'var(--theme-text-secondary)' }}>
                          GRN: {line.grn.quantity} × R{line.grn.unit_price.toFixed(2)} = R{line.grn.total.toFixed(2)}
                        </p>
                      )}
                      {line.expense && (
                        <p style={{ color: 'var(--theme-text-secondary)' }}>
                          Inv: {line.expense.quantity} × R{line.expense.unit_price.toFixed(2)} = R{line.expense.total.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
